//Built-in libraries
import { useState } from 'react';
import { useEffect } from 'react';

//Third party libraries
import { ExclamationIcon, IdentificationIcon } from '@heroicons/react/outline';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

//Custom components
import ForgotPassword from './ForgotPassword';
import PasswordResetRequested from './PasswordResetRequested';
import Loading from '../../components/UI/loading';
import Button from '../../components/UI/Button';
import SignIn from '../../pages/Auth/SignIn';

//Services
import { authSelector } from '../../store';

//Assets and Miscellaneous
import { useCurrentAccountData } from '../../hooks/ReactQueryHooks/useCurrentAccountQuery';
import { sanitizeResponseError } from '../../utils';
import { ReplyIcon } from '@heroicons/react/solid';

//Component Begin
const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const subdomains = window.location.hostname.split('.');
  const [error, setError] = useState('');
  const { isLoggedIn } = useSelector(authSelector);
  const getCurrentAccountInfo = useCurrentAccountData({
    onError: (resError: any) => {
      const sanitizedError = sanitizeResponseError(resError);
      setError(
        `Un oh! we encountered an error trying to find your account at SAGE Terminal. Reason: ${sanitizedError}`
      );
    },
  });

  const currentAccountInfo = getCurrentAccountInfo.data?.data;

  useEffect(() => {
    if (isLoggedIn) {
      let redirectPath =
        currentAccountInfo?.subdomain == 'sageadmin'
          ? '/app/clients'
          : '/app/dashboard';
      if (location.state !== null) {
        redirectPath = location.state.from.pathname;
      }
      navigate(redirectPath, { replace: true });
    }

    if (subdomains.length <= 2) {
      setError(
        'Subdomain is not provided. Account subdomain is required to Sign In.'
      );
    }
  }, [subdomains, isLoggedIn]);

  const backToRootHandler = () => {
    const hostname = window.location.host.split('.');
    const lastItem = hostname[hostname.length - 1];
    const secondLastItem = hostname[hostname.length - 2];
    const rootDomain = secondLastItem + '.' + lastItem;
    window.location.href = `${window.location.protocol}//${rootDomain}`;
  };

  const renderError = () => {
    return (
      <section className='flex-grow flex justify-center items-center text-neutralColor-normal'>
        <div className='flex flex-row items-start gap-4 justify-center mx-auto bg-white w-11/12 md:w-2/3 lg:w-3/5 max-w-lg shadow-2xl rounded-lg px-4 pt-6 pb-8 border-t-2 '>
          <span className='bg-red-100 rounded-full p-2'>
            <ExclamationIcon className='h-6 w-6 text-tertiaryColor-normal' />
          </span>

          <div className='flex flex-col justify-center'>
            <h2 className='m-0 font-bold'>Account Error</h2>
            <p className='my-5'>{error}</p>
            <div>
              <Button
                label='Go back to home'
                btnStyle='btn-primary'
                type='button'
                icon={<ReplyIcon className='h-4 w-4' />}
                onClick={backToRootHandler}
              />
            </div>
          </div>
        </div>
      </section>
    );
  };

  function nextSlide(id: string) {
    let activeSlide = document.getElementById(id);
    activeSlide!.classList.remove('translate-x-0');
    activeSlide!.classList.add('-translate-x-full');
    if (id === 'signIn') {
      let nextSlide = document.getElementById('forgotPassword');
      nextSlide!.classList.remove('translate-x-full');
      nextSlide!.classList.add('translate-x-0');
    } else if (id === 'forgotPassword') {
      let nextSlide = document.getElementById('passwordResetRequested');
      nextSlide!.classList.remove('translate-x-full');
      nextSlide!.classList.add('translate-x-0');
    } else if (id === 'passwordResetRequested') {
      let nextSlide = document.getElementById('signIn');
      nextSlide!.classList.remove('-translate-x-full');
      nextSlide!.classList.add('translate-x-0');

      let allSlide = document.getElementsByClassName('slide');
      for (let i = 0; i < allSlide.length; i++) {
        allSlide[i].classList.remove('-translate-x-full');
        allSlide[i].classList.add('translate-x-full');
      }
    } else {
      let nextSlide = activeSlide!.nextElementSibling;
      nextSlide!.classList.remove('translate-x-full');
      nextSlide!.classList.add('translate-x-0');
    }
  }

  function previousSlide(id: string) {
    let activeSlide = document.getElementById(id);
    activeSlide!.classList.remove('translate-x-0');
    activeSlide!.classList.add('translate-x-full');

    let previousSlide = activeSlide!.previousElementSibling;
    previousSlide!.classList.remove('-translate-x-full');
    previousSlide!.classList.add('translate-x-0');
  }

  const renderContent = () => {
    return (
      <div className='h-full grid items-end overflow-y-scroll overflow-x-hidden'>
        <div className='mx-auto text-center'>
          {getCurrentAccountInfo.isSuccess &&
            currentAccountInfo?.companyLogoUrl && (
              <img
                className='object-fit w-40 h-14 mx-auto'
                src={currentAccountInfo?.companyLogoUrl}
                alt='Client logo'
              />
            )}
          {getCurrentAccountInfo.isSuccess &&
            currentAccountInfo?.companyLogoUrl === null && (
              <img
                className='object-scale-down w-40 lg:w-60 mx-auto'
                src='https://assets.seinterminal.com/images/logos/SEIN_Tech_Partners_Logo_Color.png'
                alt='sein technology partners logo'
              />
            )}
          {getCurrentAccountInfo.isLoading && (
            <IdentificationIcon
              className='h-16 w-16 animate-pulse mx-auto text-primaryColor-normal'
              style={{ animationDelay: '0.2s' }}
            />
          )}
          <p className='mb-5 lg:text-lg text-center font-bold text-neutralColor-normal'>
            Welcome to{' '}
            {getCurrentAccountInfo.isLoading ? (
              <span>
                <span
                  className='animate-pulse'
                  style={{ animationDelay: '0.2s' }}
                >
                  .
                </span>
                <span
                  className='animate-pulse'
                  style={{ animationDelay: '0.4s' }}
                >
                  .
                </span>
                <span
                  className='animate-pulse'
                  style={{ animationDelay: '0.6s' }}
                >
                  .
                </span>
              </span>
            ) : (
              currentAccountInfo?.companyName
            )}{' '}
            Portal
          </p>
        </div>
        <div
          className={`h-full row-span-5 sm:row-span-4 inset-0 w-full ${
            error.length > 0 ? 'hidden' : 'relative'
          }`}
        >
          <div
            id='signIn'
            className='absolute inset-0 flex items-start justify-center transition-all ease-in-out duration-1000 transform translate-x-0'
          >
            <SignIn slideHandler={() => nextSlide('signIn')} />
          </div>
          <div
            id='forgotPassword'
            className='absolute inset-0 flex items-start justify-center transition-all ease-in-out duration-1000 transform  translate-x-full slide'
          >
            <ForgotPassword
              backHandler={() => previousSlide('forgotPassword')}
              forwardHandler={() => nextSlide('forgotPassword')}
            />
          </div>
          <div
            id='passwordResetRequested'
            className='absolute inset-0 flex items-start justify-center transition-all ease-in-out duration-1000 transform  translate-x-full slide'
          >
            <PasswordResetRequested
              forwardHandler={() => nextSlide('passwordResetRequested')}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className='display flex flex-col justify-between h-screen'>
      {getCurrentAccountInfo.isLoading && <Loading loadingText='' />}
      {(getCurrentAccountInfo.isError || error) && renderError()}
      {getCurrentAccountInfo.isSuccess && renderContent()}
    </div>
  );
};

export default Home;
